import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/store/auth';
import { useAuthStore } from '../store/useAuthStore';
import { LoginCredentials, RegisterData } from '../domain';
import { useEffect } from 'react';

export const useSession = () => {
    const queryClient = useQueryClient();
    const { setState, resetState, user, isAuthenticated, role } = useAuthStore();

    //  Query principal de sesión
    const sessionQuery = useQuery({
        queryKey: ['session'],
        queryFn: () => authApi.getProfile(),
        staleTime: 30 * 60 * 1000, // 30 min de caché para evitar validaciones excesivas
        retry: false,
        enabled: isAuthenticated, // Mejor Práctica Lighthouse: Evita 401 preventivo
    });

    //  Sincronizar Zustand con el resultado de React Query
    useEffect(() => {
        if (sessionQuery.data) {
            setState({
                user: sessionQuery.data,
                role: sessionQuery.data.role,
                isAuthenticated: true,
                loading: false,
                error: null,
            });
        } else if (sessionQuery.isFetched && sessionQuery.data === null) {
            // Solo resetear cuando el servidor confirmó explícitamente que no hay sesión
            // (data === null, no cuando hay un error de red o de token aún pendiente de refresh)
            resetState();
        }
        // NOTA: sessionQuery.isError NO resetea el estado porque el interceptor de axiosClient
        // ya intenta hacer refresh automáticamente. Si el refresh falla,
        // el interceptor setea queryClient.setQueryData(['session'], null),
        // lo que finalmente dispara el caso data === null arriba.
    }, [sessionQuery.data, sessionQuery.isFetched, setState, resetState]);

    //  Mutation para Login
    const loginMutation = useMutation({
        mutationFn: (credentials: LoginCredentials) => authApi.login(credentials),
        onSuccess: () => {
            // Invalidar sesión para forzar recarga del perfil
            queryClient.invalidateQueries({ queryKey: ['session'] });
        }
    });

    //  Mutation para Registro
    const registerMutation = useMutation({
        mutationFn: (data: RegisterData) => authApi.register(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['session'] });
        }
    });

    //  Mutation para Logout
    const logoutMutation = useMutation({
        mutationFn: () => authApi.logout(),
        onSuccess: () => {
            resetState();
            queryClient.setQueryData(['session'], null);
            queryClient.invalidateQueries({ queryKey: ['session'] });
        }
    });

    // Derivar el estado de forma síncrona para evitar race conditions 
    // entre el fin de la query y el useEffect que actualiza el store
    const currentUser = sessionQuery.data || user;
    const currentIsAuthenticated = !!sessionQuery.data || isAuthenticated;
    const currentRole = sessionQuery.data?.role || role;

    return {
        user: currentUser,
        isAuthenticated: currentIsAuthenticated,
        role: currentRole,
        isLoading: sessionQuery.isLoading || loginMutation.isPending,
        isRefetching: sessionQuery.isRefetching,
        error: sessionQuery.error || loginMutation.error,

        // Acciones
        login: loginMutation.mutateAsync,
        register: registerMutation.mutateAsync,
        logout: logoutMutation.mutateAsync,
        refreshSession: () => sessionQuery.refetch(),
    };
};
